import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';

import { TrackerService } from '@services/tracker.service';
import { AuthOutcome, isAuthenticated } from 'src/app/common/contracts/authentication/auth-outcome';
import makeDebug from '../../../../makeDebug';
import { AuthService } from '../../services/auth.service';
import { ISyncContext } from '../contracts/sync/sync-context';
import { ISyncElement, SyncElementResult } from '../contracts/sync/sync-element';
import { Authenticator as Authentication } from '../contracts/sync/sync-endpoints';

const debug = makeDebug('sync:authenticator');

@Injectable()
export class Authenticator implements ISyncElement {
  public get name(): string {
    return Authentication;
  }

  constructor(
    private _authenticationService: AuthService,
    private _router: Router,
    private _trackerService: TrackerService,
    private _ngZone: NgZone
  ) {}

  public async execute(context: ISyncContext): Promise<SyncElementResult> {
    debug('entered execute function', context);
    const rid = context.syncRunId || 'unknown';
    debug(`[${rid}] execute | isOnline=${context.params.isOnline} isLogin=${context.params.isLogin}`);
    window.logger.addBreadcrumb({
      message: `[Authenticator][${rid}] execute | isOnline=${context.params.isOnline} isLogin=${context.params.isLogin}`,
    });
    const outcome = await this.authenticate(context.params.isOnline, rid);
    const authenticated = isAuthenticated(outcome);
    // In my opinion we need to login if the authenticate didnt work
    // otherwise auth service holds undefined values but we start the sync pipeline which causes errors
    // In offline case the user is set from storage and authenticate resolves true
    // If offline and not user in storage we should not open the app
    if (!authenticated) {
      // Every non-authenticated outcome still ends on /login — unchanged (#26615). New is that the
      // *reason* is reported: the share of these redirects that are not real logouts
      // (Unavailable, NeedsOnlineReauth) becomes measurable before any behaviour is changed.
      this._trackerService.debugSyncPipelineElementNotExecuted(this.name, `notAuthenticated:${outcome}`);
      debug(`[${rid}] User could not be authenticated (${outcome}).`);
      window.logger.addBreadcrumb({ message: `[Authenticator][${rid}] navigating to /login | outcome=${outcome}` });
      await this._ngZone.run(() => this._router.navigate(['/login']));
      context.params.noAuth = true;
    }
    return { contextId: context.id, authenticated, jobDone: true };
  }

  private async authenticate(isOnline: boolean, rid: string): Promise<AuthOutcome> {
    debug(`[${rid}] configure()`);
    window.logger.addBreadcrumb({ message: `[Authenticator][${rid}] configure()` });
    await this._authenticationService.configure();
    debug(`[${rid}] authenticate(isOnline=${isOnline})`);
    window.logger.addBreadcrumb({ message: `[Authenticator][${rid}] authenticate(isOnline=${isOnline})` });
    let outcome = await this._authenticationService.authenticateWithOutcome(isOnline);
    debug(`[${rid}] authenticate result=${outcome}`);
    window.logger.addBreadcrumb({ message: `[Authenticator][${rid}] authenticate result=${outcome}` });
    // tryRefresh() instead of refreshTokenMobile(): the latter returns false in the browser, so
    // the web never got a refresh retry here at all (#26615).
    if (!isAuthenticated(outcome) && isOnline && (await this._authenticationService.tryRefresh())) {
      // call configure again on mobile to create new defered with access token
      // which is waited for in authenticate();
      await this._authenticationService.configure();
      debug(`[${rid}] accessToken refreshed, retry authenticate`);
      window.logger.addBreadcrumb({ message: `[Authenticator][${rid}] retry authenticate after refresh` });
      outcome = await this._authenticationService.authenticateWithOutcome(isOnline);
      debug(`[${rid}] retry result=${outcome}`);
      window.logger.addBreadcrumb({ message: `[Authenticator][${rid}] retry result=${outcome}` });
    }
    const isAuthenticatedFlag = isAuthenticated(outcome);
    debug(`[${rid}] setAuthenticated(${isAuthenticatedFlag}, ${isOnline})`);
    window.logger.addBreadcrumb({
      message: `[Authenticator][${rid}] setAuthenticated(${isAuthenticatedFlag}, ${isOnline}) | outcome=${outcome}`,
    });
    this._authenticationService.setAuthenticated(isAuthenticatedFlag, isOnline);
    return outcome;
  }
}
